<template>
  <LabelValue :label="t('Reviews.agency.edit.services.label').toString()" show-icon>
    <template #hint>
      <span v-html="t('Reviews.agency.edit.services.hint')" />
    </template>
    <div class="mt-6 grid grid-cols-3 gap-6">
      <CircleSpinner v-if="loading" class="justify-self-center" />
      <div v-else>
        <div v-for="category in categories" :key="category.id">
          <div v-if="availableFeaturesByCategory[category.id]?.length" class="relative mb-6 rounded-md border border-solid border-main p-6">
            <FeaturesEditForm
              :available-features="availableFeaturesByCategory[category.id] || []"
              :category-id="category.id"
              :categories="categories"
              :features="services[category.id] || []"
            />
          </div>
        </div>
      </div>
      <PreviewBox class="col-span-2" :label="t('Reviews.agency.view.industriesAndServices.services.label')">
        <FeaturesByCategoriesView :assigned-features="assignedFeatures" />
      </PreviewBox>
    </div>
  </LabelValue>
</template>
<script setup lang="ts">
import { CircleSpinner } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PreviewBox from '@/components/ReviewsManagement/PreviewBox.vue'
import { type AgencyData, useReviewsAgencyFeaturesQuery } from '@/gql/reviews'
import LabelValue from '@/ui/LabelValue.vue'

import FeaturesByCategoriesView from './FeaturesByCategoriesView.vue'
import FeaturesEditForm from './FeaturesEditForm.vue'
import type { FeatureFormData } from './types'

const props = defineProps<{
  services: Record<string, FeatureFormData[]>
  categories: { id: string; title: string }[]
}>()

const { t } = useI18n()

const agencyFeaturesQueryVariables = computed(() => ({
  categories: props.categories.map(({ id }) => id),
}))

const { result: categoryFeatures, loading } = useReviewsAgencyFeaturesQuery(agencyFeaturesQueryVariables, {
  clientId: 'reviews',
})

const availableFeaturesByCategory = computed(() => {
  const featuresByCategory: Record<string, { id: string; name: string }[]> = {}
  categoryFeatures.value?.agencyFeatures?.forEach((feature) => {
    if (feature.category?.id) {
      if (!featuresByCategory[feature.category.id]) {
        featuresByCategory[feature.category.id] = []
      }
      featuresByCategory[feature.category.id].push(feature)
    }
  })
  return featuresByCategory
})

const assignedFeatures = computed(() => {
  return Object.entries(props.services).reduce(
    (acc, [categoryId, features]) => {
      const categoryTitle = props.categories.find((category) => category.id === categoryId)?.title || ''
      features.forEach((feature) => {
        if (feature.featureId && !feature._destroy) {
          const featureName = availableFeaturesByCategory.value[categoryId]?.find((f) => f.id === feature.featureId)?.name || ''
          acc.push({
            position: feature.position,
            feature: { id: feature.featureId, name: featureName, category: { id: categoryId, title: categoryTitle } },
          })
        }
      })
      return acc
    },
    [] as AgencyData['assignedFeatures'],
  )
})
</script>
